import React, { useEffect, useState, type FC } from 'react';

import { useCommentsPanelActions } from '@confluence/comments-panel-utils';
import { expValEquals } from '@confluence/feature-experiments';
import type { LoadingPriority } from '@confluence/loadable';
import { LoadablePaint, LoadingPriority as LoadingPriorityEnum } from '@confluence/loadable';

import { ObjectCommentContainer } from './ObjectCommentContainer';

type CommentTypes = {
	showGeneralComment?: boolean;
	pageWidthType?: string;
	loadingPriority?: LoadingPriority;
	showLabels: boolean;
};

const ObjectCommentContainerLoader = LoadablePaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-ObjectCommentContainer" */ './ObjectCommentContainer'
			)
		).ObjectCommentContainer,
});

export const ObjectCommentWrapper: FC<CommentTypes> = ({
	showGeneralComment,
	pageWidthType,
	loadingPriority,
	showLabels,
}) => {
	const [shouldForceLoadComments, setShouldForceLoadComments] = useState(false);
	const { setShowGeneralComments } = useCommentsPanelActions();

	useEffect(() => {
		setShowGeneralComments(showGeneralComment);
		if (expValEquals('confluence_comment_force_render_footer_on_idle_exp', 'cohort', 'test')) {
			scheduleLoadFooterComments();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- we only want to set the comment type once on the comment mount
	}, []);

	const scheduleLoadFooterComments = () => {
		if ('requestIdleCallback' in window) {
			requestIdleCallback((deadline) => {
				if (deadline.timeRemaining() > 0) {
					setShouldForceLoadComments(true);
				}
			});
		}
	};

	return loadingPriority === LoadingPriorityEnum.PAINT ? (
		<ObjectCommentContainerLoader
			pageWidthType={pageWidthType}
			showLabels={showLabels}
			shouldForceLoadComments={shouldForceLoadComments}
		/>
	) : (
		<ObjectCommentContainer
			pageWidthType={pageWidthType}
			showLabels={showLabels}
			shouldForceLoadComments={shouldForceLoadComments}
		/>
	);
};
